import { useMemo, useState } from 'react';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { IconButton, WidgetAction } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';

import { fetchFilterCustomization } from '../../api/portalGateway/tableFilterCustomization';
import { CUSTOM_FILTER_INSTANCE_ID_SUFFIX } from '../../utils/constants';
import { TableCustomFilter } from '../tablecustomization/TableCustomFilter';

type CustomPageFilterModalProps = {
  page: PageDto;
  filterInstanceId: string | null;
};

export function CustomPageFilterModal({
  page,
  filterInstanceId,
}: CustomPageFilterModalProps) {
  const [isCustomPageFilterModalOpen, setIsCustomPageFilterModalOpen] =
    useState(false);

  const showCustomPageFilterIcon = useMemo(() => {
    return (
      page.pageFilter?.customizablePageFilters === true &&
      page.pageFilter?.customPageFilterColumns != null &&
      page.pageFilter?.customPageFilterColumns.length > 0
    );
  }, [page.pageFilter]);

  const customizableColumns = useMemo(() => {
    return (
      page.pageFilter?.customPageFilterColumns
        ?.map((filterColumn) => ({
          ...filterColumn,
          // disable customization for configured page filter columns
          disabled: page.pageFilter?.filters.some(
            (filter) => filter.key === filterColumn.key
          ),
        }))
        // sort configured page filter items to the top
        .sort((a, b) => Number(b.disabled) - Number(a.disabled)) ?? []
    );
  }, [page?.pageFilter]);

  const { data: storedCustomFilterData } = useQuery({
    queryKey: [
      'filterCustomization',
      { referenceType: 'page', reference: filterInstanceId ?? page.id },
    ],
    queryFn: fetchFilterCustomization,
    enabled: customizableColumns.length > 0,
  });

  const activeCustomFilterIds = useMemo(
    () =>
      storedCustomFilterData?.data.columns
        .map((c) => c.columnId)
        .filter((c) => customizableColumns.find((i) => i.id === c)) ?? [],
    [customizableColumns, storedCustomFilterData?.data.columns]
  );

  const customPageFilterAction: WidgetAction = {
    title: 'Custom page filters',
    icon: {
      name: activeCustomFilterIds.length > 0 ? 'filter-active' : 'filter',
      svgIcon: true,
    },
  };

  return (
    <>
      {showCustomPageFilterIcon && (
        <IconButton
          action={customPageFilterAction}
          key={customPageFilterAction.icon.name}
          loading={false}
          onClick={() => {
            setIsCustomPageFilterModalOpen(true);
          }}
        />
      )}
      <TableCustomFilter
        activeCustomFilterIds={activeCustomFilterIds}
        customFilterColumns={customizableColumns}
        modalOpened={isCustomPageFilterModalOpen}
        referenceType="page"
        onClose={() => {
          setIsCustomPageFilterModalOpen(false);
        }}
        reference={filterInstanceId ?? page.id}
        filterInstanceIds={[
          `${filterInstanceId ?? page.id}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`,
        ]}
      />
    </>
  );
}
